import { create } from 'zustand'

const getFirmData = firm => {
  if (firm.firmType === 'P7' && firm.p7Firm) return firm.p7Firm
  if (firm.firmType === 'TREG' && firm.tregFirm) return firm.tregFirm
  return firm
}

const isSameFirm = (firm1, firm2) => {
  const data1 = getFirmData(firm1)
  const data2 = getFirmData(firm2)

  if (data1.id && data2.id) {
    return data1.id === data2.id
  }

  if (data1.url_code && data2.url_code) {
    return data1.url_code === data2.url_code
  }

  const emails1 = data1.emailList || []
  const emails2 = data2.emailList || []

  return emails1.some(email => emails2.includes(email))
}

const useFirmSelectionStore = create((set, get) => ({
  firmSelections: {},

  getSelectionMode: (firmKey) => {
    const state = get()
    const firmSelection = state.firmSelections[firmKey]

    if (!firmSelection) return 'include'

    return firmSelection.isSelectAllActive
      ? (firmSelection.excludedFirms.length > 0 ? 'exclude' : 'all')
      : 'include'
  },

  getSelectionData: (firmKey) => {
    const state = get()
    const firmSelection = state.firmSelections[firmKey]

    if (!firmSelection) return { mode: 'include', items: [] }

    const mode = state.getSelectionMode(firmKey)

    switch (mode) {
      case 'all':
        return { mode: 'all', items: [] }
      case 'exclude':
        return { mode: 'exclude', items: firmSelection.excludedFirms }
      case 'include':
      default:
        return { mode: 'include', items: firmSelection.includedFirms }
    }
  },

  getAllSelections: () => {
    const state = get()
    const allSelections = {}

    Object.keys(state.firmSelections).forEach(firmKey => {
      allSelections[firmKey] = state.getSelectionData(firmKey)
    })

    return allSelections
  },

  getCurrentPageSelectionStatus: (firmKey, currentPageFirms) => {
    const state = get()
    const selectionMode = state.getSelectionMode(firmKey)
    const firmSelection = state.firmSelections[firmKey]

    if (!currentPageFirms || currentPageFirms.length === 0) {
      return { isAllSelected: false, isPartiallySelected: false }
    }

    const isFirmInList = (list, target) => {
      return list.some(firm => isSameFirm(firm, target))
    }

    const includedFirms = firmSelection?.includedFirms || []
    const excludedFirms = firmSelection?.excludedFirms || []

    const checkedCount = currentPageFirms.filter(firmObject => {
      if (selectionMode === 'all') {
        return true
      } else if (selectionMode === 'exclude') {
        return !isFirmInList(excludedFirms, firmObject)
      } else {
        return isFirmInList(includedFirms, firmObject)
      }
    }).length

    const isAllSelected = checkedCount === currentPageFirms.length
    const isPartiallySelected = checkedCount > 0 && checkedCount < currentPageFirms.length

    return { isAllSelected, isPartiallySelected }
  },

  isRowSelected: (firmKey, firmObject) => {
    const state = get()
    const selectionMode = state.getSelectionMode(firmKey)
    const firmSelection = state.firmSelections[firmKey]

    if (!firmSelection) return false

    const includedFirms = firmSelection.includedFirms || []
    const excludedFirms = firmSelection.excludedFirms || []

    if (selectionMode === 'all') {
      return !excludedFirms.some(f => isSameFirm(f, firmObject))
    } else if (selectionMode === 'exclude') {
      return !excludedFirms.some(f => isSameFirm(f, firmObject))
    } else {
      return includedFirms.some(f => isSameFirm(f, firmObject))
    }
  },

  // Actions
  actions: {
    handleSelectAllToggle: (firmKey, checked) => {
      const state = get()
      const newFirmSelections = { ...state.firmSelections }

      newFirmSelections[firmKey] = {
        isSelectAllActive: checked,
        includedFirms: [],
        excludedFirms: []
      }

      set({ firmSelections: newFirmSelections })
    },

    handleSelectCurrentPageToggle: (firmKey, checked, currentPageFirms) => {
      const state = get()
      const selectionMode = state.getSelectionMode(firmKey)

      const newFirmSelections = { ...state.firmSelections }

      const firmSelection = newFirmSelections[firmKey] || {
        isSelectAllActive: false,
        includedFirms: [],
        excludedFirms: []
      }

      if (checked) {
        // Select all firms on current page
        if (selectionMode === 'all' || selectionMode === 'exclude') {
          // In 'all' or 'exclude' mode, remove current page firms from excluded list
          const newExcluded = firmSelection.excludedFirms.filter(
            excludedFirm => !currentPageFirms.some(pageFirm => isSameFirm(excludedFirm, pageFirm))
          )

          newFirmSelections[firmKey] = {
            ...firmSelection,
            excludedFirms: newExcluded
          }
        } else {
          // In 'include' mode, add all current page firms to included list
          const newIncluded = [...firmSelection.includedFirms]

          currentPageFirms.forEach(pageFirm => {
            const exists = newIncluded.some(firm => isSameFirm(firm, pageFirm))
            if (!exists) {
              newIncluded.push(pageFirm)
            }
          })

          newFirmSelections[firmKey] = {
            ...firmSelection,
            includedFirms: newIncluded
          }
        }
      } else {
        // Deselect all firms on current page
        if (selectionMode === 'all' || selectionMode === 'exclude') {
          // In 'all' or 'exclude' mode, add current page firms to excluded list
          const newExcluded = [...firmSelection.excludedFirms]

          currentPageFirms.forEach(pageFirm => {
            const exists = newExcluded.some(firm => isSameFirm(firm, pageFirm))
            if (!exists) {
              newExcluded.push(pageFirm)
            }
          })

          newFirmSelections[firmKey] = {
            ...firmSelection,
            excludedFirms: newExcluded
          }
        } else {
          // In 'include' mode, remove all current page firms from included list
          const newIncluded = firmSelection.includedFirms.filter(
            includedFirm => !currentPageFirms.some(pageFirm => isSameFirm(includedFirm, pageFirm))
          )

          newFirmSelections[firmKey] = {
            ...firmSelection,
            includedFirms: newIncluded
          }
        }
      }

      set({ firmSelections: newFirmSelections })
    },

    handleRowToggle: (firmKey, firmObject, isChecked) => {
      const state = get()
      const selectionMode = state.getSelectionMode(firmKey)

      const newFirmSelections = { ...state.firmSelections }

      const firmSelection = newFirmSelections[firmKey] || {
        isSelectAllActive: false,
        includedFirms: [],
        excludedFirms: []
      }

      if (selectionMode === 'all') {
        const newExcluded = [...firmSelection.excludedFirms]

        const existingIndex = newExcluded.findIndex(f => isSameFirm(f, firmObject))

        if (existingIndex === -1) {
          newExcluded.push(firmObject)
        }

        newFirmSelections[firmKey] = { ...firmSelection, excludedFirms: newExcluded }

        set({ firmSelections: newFirmSelections })
      } else if (selectionMode === 'exclude') {
        let newExcluded = [...firmSelection.excludedFirms]

        if (isChecked) {
          newExcluded = newExcluded.filter(f => !isSameFirm(f, firmObject))
        } else {
          const existingIndex = newExcluded.findIndex(f => isSameFirm(f, firmObject))

          if (existingIndex === -1) {
            newExcluded.push(firmObject)
          }
        }

        if (newExcluded.length === 0) {
          newFirmSelections[firmKey] = {
            ...firmSelection,
            isSelectAllActive: true,
            excludedFirms: []
          }
        } else {
          newFirmSelections[firmKey] = { ...firmSelection, excludedFirms: newExcluded }
        }
        set({ firmSelections: newFirmSelections })
      } else {
        let newIncluded = [...firmSelection.includedFirms]

        if (isChecked) {
          const existingIndex = newIncluded.findIndex(f => isSameFirm(f, firmObject))

          if (existingIndex === -1) {
            newIncluded.push(firmObject)
          }
        } else {
          newIncluded = newIncluded.filter(f => !isSameFirm(f, firmObject))
        }

        newFirmSelections[firmKey] = { ...firmSelection, includedFirms: newIncluded }

        set({ firmSelections: newFirmSelections })
      }
    },

    resetSelection: () => {
      set({
        firmSelections: {}
      })
    }
  }
}))

export const useFirmSelectionState = (firmKey) => useFirmSelectionStore((state) => {
  const firmSelection = state.firmSelections[firmKey]
  return {
    isSelectAllActive: firmSelection?.isSelectAllActive || false,
    includedFirms: firmSelection?.includedFirms || [],
    excludedFirms: firmSelection?.excludedFirms || [],
    selectionMode: state.getSelectionMode(firmKey),
    selectionData: state.getSelectionData(firmKey)
  }
})

export const useFirmSelectionActions = () => useFirmSelectionStore((state) => state.actions)

export const useAllSelections = () => useFirmSelectionStore((state) => state.getAllSelections())

export const getFirmSelectionStore = () => useFirmSelectionStore.getState()

export default useFirmSelectionStore
